import { useState } from 'react';
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons/faTrashCan';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Button, Group, Pill, PillsInput, Stack, Switch, Table, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { notificationsSectionMessages } from './notifications-section-messages';
import styles from './NotificationsSection.module.css';
import { NotificationUpdatesPicker } from './NotificationUpdatesPicker';
import { FIELD_MAX_LENGTHS, VALIDATION_PATTERNS } from '../../../../address/validation';
import { Tag } from '../../../tag/tag';

import type { FreightNotificationType } from '../../../../types/freight-responses';
import type { WizardNotificationConfig, WizardNotifications } from '../../freight-booking-wizard-types';

type EditingEmailState = {
  recipientId: string;
  index: number;
  draft: string;
};

const MAX_EMAILS_PER_GROUP = 5;

const createEmptyConfig = (): WizardNotificationConfig => ({
  id: crypto.randomUUID(),
  emails: [],
  notificationTypes: [],
});

export type NotificationsSectionProps = {
  value: WizardNotifications;
  onChange: (next: WizardNotifications) => void;
};

export const NotificationsSection = ({ value, onChange }: NotificationsSectionProps) => {
  const intl = useIntl();
  const [rowIdsWithRejectedEmail, setRowIdsWithRejectedEmail] = useState<string[]>([]);
  const [rowIdsWithMaxEmails, setRowIdsWithMaxEmails] = useState<string[]>([]);
  const [draftEmailInputs, setDraftEmailInputs] = useState<Record<string, string>>({});
  const [editingEmail, setEditingEmail] = useState<EditingEmailState | null>(null);

  const updateRecipients = (recipientConfigs: WizardNotificationConfig[]) => onChange({ ...value, recipientConfigs });

  const handleToggle = (isEnabled: boolean) =>
    onChange({
      isEnabled,
      recipientConfigs: isEnabled && value.recipientConfigs.length === 0 ? [createEmptyConfig()] : value.recipientConfigs,
    });

  const handleAddRecipient = () => updateRecipients([...value.recipientConfigs, createEmptyConfig()]);

  const handleRemoveRecipient = (id: string) => updateRecipients(value.recipientConfigs.filter((config) => config.id !== id));

  const handleChangeEmails = (id: string, submittedEmails: string[]) => {
    const hasRejected = submittedEmails.some((email) => !VALIDATION_PATTERNS.EMAIL.test(email.trim()));
    setRowIdsWithRejectedEmail((previous) => (hasRejected ? [...new Set([...previous, id])] : previous.filter((rowId) => rowId !== id)));
    const wasMaxExceeded = submittedEmails.length > MAX_EMAILS_PER_GROUP;
    const cappedEmails = submittedEmails.slice(0, MAX_EMAILS_PER_GROUP);
    setRowIdsWithMaxEmails((previous) => (wasMaxExceeded ? [...new Set([...previous, id])] : previous.filter((rowId) => rowId !== id)));
    updateRecipients(value.recipientConfigs.map((recipient) => (recipient.id === id ? { ...recipient, emails: cappedEmails } : recipient)));
  };

  const handleChangeTypes = (id: string, notificationTypes: FreightNotificationType[]) =>
    updateRecipients(value.recipientConfigs.map((recipient) => (recipient.id === id ? { ...recipient, notificationTypes } : recipient)));

  const getDraftEmail = (id: string) => draftEmailInputs[id] ?? '';

  const commitDraftEmail = (id: string) => {
    const draft = getDraftEmail(id).trim();
    if (!draft) return;
    const recipient = value.recipientConfigs.find((r) => r.id === id);
    if (recipient) {
      handleChangeEmails(id, [...recipient.emails, draft]);
    }
    setDraftEmailInputs((previous) => ({ ...previous, [id]: '' }));
  };

  const handleStartEditEmail = (recipientId: string, index: number, email: string) => {
    setEditingEmail({ recipientId, index, draft: email });
  };

  const handleCommitEditEmail = () => {
    if (!editingEmail) return;
    const { recipientId, index, draft } = editingEmail;
    const trimmed = draft.trim();
    const config = value.recipientConfigs.find((r) => r.id === recipientId);
    if (config) {
      if (trimmed === '') {
        handleChangeEmails(
          recipientId,
          config.emails.filter((_, i) => i !== index),
        );
      } else {
        handleChangeEmails(
          recipientId,
          config.emails.map((e, i) => (i === index ? trimmed : e)),
        );
      }
    }
    setEditingEmail(null);
  };

  const handleCancelEditEmail = () => setEditingEmail(null);

  const handleRemoveEmail = (id: string, email: string) => {
    const recipient = value.recipientConfigs.find((r) => r.id === id);
    if (recipient) {
      handleChangeEmails(
        id,
        recipient.emails.filter((existingEmail) => existingEmail !== email),
      );
    }
  };

  const handleEmailInputChange = (id: string, inputValue: string) => {
    const hasSplitChar = /[,; ]/.test(inputValue);
    if (hasSplitChar) {
      const parts = inputValue.split(/[,; ]+/).filter(Boolean);
      const recipient = value.recipientConfigs.find((r) => r.id === id);
      if (recipient) {
        handleChangeEmails(id, [...recipient.emails, ...parts]);
      }
      setDraftEmailInputs((previous) => ({ ...previous, [id]: '' }));
    } else {
      setDraftEmailInputs((previous) => ({ ...previous, [id]: inputValue }));
    }
  };

  const getEmailError = (recipient: WizardNotificationConfig) => {
    if (rowIdsWithMaxEmails.includes(recipient.id)) {
      return intl.formatMessage(notificationsSectionMessages.maxEmailsError);
    }
    if (rowIdsWithRejectedEmail.includes(recipient.id)) {
      return intl.formatMessage(notificationsSectionMessages.invalidEmailError);
    }
    if (recipient.notificationTypes.length > 0 && recipient.emails.length === 0) {
      return intl.formatMessage(notificationsSectionMessages.noEmailsError);
    }
    return undefined;
  };

  return (
    <Stack className={styles.section} data-testid='notifications-section' gap='md' pt='md'>
      <Stack gap={2}>
        <Switch
          checked={value.isEnabled}
          classNames={{ label: styles.switchLabel }}
          data-testid='notifications-toggle'
          label={intl.formatMessage(notificationsSectionMessages.sectionTitle)}
          onChange={(event) => handleToggle(event.currentTarget.checked)}
        />
        <Text c='var(--adl-text-secondary)' size='sm'>
          {intl.formatMessage(notificationsSectionMessages.description)}
        </Text>
      </Stack>

      {value.isEnabled && (
        <Stack gap='sm'>
          <Text c='var(--adl-text-secondary)' size='sm'>
            {intl.formatMessage(notificationsSectionMessages.helperText)}
          </Text>

          <Table data-testid='notifications-table' withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{intl.formatMessage(notificationsSectionMessages.emailColumnHeader)}</Table.Th>
                <Table.Th>{intl.formatMessage(notificationsSectionMessages.updatesColumnHeader)}</Table.Th>
                <Table.Th className={styles.actionColumn} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {value.recipientConfigs.map((recipient) => (
                <Table.Tr key={recipient.id}>
                  <Table.Td className={styles.emailCell}>
                    <PillsInput data-testid={`notification-emails-${recipient.id}`} error={getEmailError(recipient)}>
                      <Pill.Group>
                        {recipient.emails.map((email, index) => {
                          const isEditing = editingEmail?.recipientId === recipient.id && editingEmail.index === index;
                          if (isEditing) {
                            return (
                              <input
                                className={styles.inlineEmailEdit}
                                ref={(el) => el?.focus()}
                                key={index}
                                onBlur={handleCommitEditEmail}
                                onChange={(event) => {
                                  const inputValue = event.currentTarget.value;
                                  setEditingEmail((prev) => (prev ? { ...prev, draft: inputValue } : null));
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleCommitEditEmail();
                                  } else if (event.key === 'Escape') {
                                    event.preventDefault();
                                    handleCancelEditEmail();
                                  }
                                }}
                                value={editingEmail.draft}
                              />
                            );
                          }
                          return (
                            <Tag.System
                              className={styles.editableEmailPill}
                              color={VALIDATION_PATTERNS.EMAIL.test(email) ? 'light' : 'alert'}
                              key={email}
                              onClick={() => handleStartEditEmail(recipient.id, index, email)}
                              onRemove={() => handleRemoveEmail(recipient.id, email)}
                              removeAriaLabel={intl.formatMessage(notificationsSectionMessages.removeEmail, { email })}
                              withRemoveButton
                            >
                              {email}
                            </Tag.System>
                          );
                        })}
                        {recipient.emails.length < MAX_EMAILS_PER_GROUP && (
                          <PillsInput.Field
                            maxLength={FIELD_MAX_LENGTHS.email}
                            onBlur={() => commitDraftEmail(recipient.id)}
                            onChange={(event) => handleEmailInputChange(recipient.id, event.currentTarget.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                commitDraftEmail(recipient.id);
                              }
                            }}
                            placeholder={
                              recipient.emails.length === 0 ? intl.formatMessage(notificationsSectionMessages.emailPlaceholder) : undefined
                            }
                            value={getDraftEmail(recipient.id)}
                          />
                        )}
                      </Pill.Group>
                    </PillsInput>
                  </Table.Td>
                  <Table.Td className={styles.updatesCell}>
                    <NotificationUpdatesPicker
                      data-testid={`notification-updates-${recipient.id}`}
                      error={
                        recipient.emails.length > 0 && recipient.notificationTypes.length === 0
                          ? intl.formatMessage(notificationsSectionMessages.noUpdatesError)
                          : undefined
                      }
                      onChange={(types) => handleChangeTypes(recipient.id, types)}
                      value={recipient.notificationTypes}
                    />
                  </Table.Td>
                  <Table.Td className={styles.actionColumn}>
                    <ActionIcon
                      aria-label={intl.formatMessage(notificationsSectionMessages.removeRecipient)}
                      data-testid={`remove-recipient-${recipient.id}`}
                      onClick={() => handleRemoveRecipient(recipient.id)}
                      variant='subtle'
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Group justify='center'>
            <Button data-testid='add-recipient' onClick={handleAddRecipient} size='compact-sm' variant='ghost'>
              {intl.formatMessage(notificationsSectionMessages.addRecipient)}
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
};
